import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { config } from '../config';
import { getApiHeaders } from '../api/apiHeaders';

const url_api = config.api.web;

/**
 * Persist chart drawings (hline / trend / note) per user + symbol.
 */
export default function useChartDrawings(symbol, userId, enabled = true) {
    const [drawings, setDrawings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const debounceTimers = useRef({});

    const clearDebounce = useCallback((id) => {
        const timer = debounceTimers.current[id];
        if (timer) {
            clearTimeout(timer);
            delete debounceTimers.current[id];
        }
    }, []);

    useEffect(() => {
        const sym = String(symbol || '').trim().toUpperCase();
        if (!enabled || !sym || !userId) {
            setDrawings([]);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError('');

        axios
            .get(`${url_api}chart_drawing`, {
                params: { id: userId, symbol: sym },
                headers: getApiHeaders(),
                withCredentials: true,
            })
            .then((response) => {
                if (cancelled) return;
                if (response.status < 300 && response.data?.status === 'Success') {
                    const list = Array.isArray(response.data.body?.list)
                        ? response.data.body.list
                        : [];
                    setDrawings(list);
                } else {
                    setDrawings([]);
                    setError('try_later');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDrawings([]);
                    setError('try_later');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            Object.keys(debounceTimers.current).forEach((key) => {
                clearTimeout(debounceTimers.current[key]);
            });
            debounceTimers.current = {};
        };
    }, [symbol, userId, enabled]);

    const createDrawing = useCallback(async (type, payload) => {
        const sym = String(symbol || '').trim().toUpperCase();
        if (!enabled || !sym || !userId) {
            return null;
        }

        const response = await axios.post(
            `${url_api}chart_drawing`,
            { id: userId, symbol: sym, type, payload },
            { headers: getApiHeaders(), withCredentials: true },
        );

        if (response.status < 300 && response.data?.status === 'Success') {
            const item = response.data.body;
            setDrawings((prev) => [...prev, item]);
            return item;
        }
        throw new Error('create_failed');
    }, [symbol, userId, enabled]);

    const updateDrawing = useCallback((drawingId, payload, { debounceMs = 400 } = {}) => {
        const sym = String(symbol || '').trim().toUpperCase();
        if (!enabled || !sym || !userId || !drawingId) {
            return;
        }

        setDrawings((prev) => prev.map((d) => (
            d.id === drawingId ? { ...d, payload } : d
        )));

        clearDebounce(drawingId);
        debounceTimers.current[drawingId] = setTimeout(async () => {
            try {
                const response = await axios.put(
                    `${url_api}chart_drawing`,
                    { id: userId, drawing_id: drawingId, payload },
                    { headers: getApiHeaders(), withCredentials: true },
                );
                if (response.status < 300 && response.data?.status === 'Success') {
                    const item = response.data.body;
                    setDrawings((prev) => prev.map((d) => (d.id === item.id ? item : d)));
                }
            } catch (e) {
                // keep optimistic local state
            } finally {
                delete debounceTimers.current[drawingId];
            }
        }, debounceMs);
    }, [symbol, userId, enabled, clearDebounce]);

    const deleteDrawing = useCallback(async (drawingId) => {
        if (!enabled || !userId || !drawingId) {
            return;
        }
        clearDebounce(drawingId);
        setDrawings((prev) => prev.filter((d) => d.id !== drawingId));
        await axios.delete(`${url_api}chart_drawing`, {
            params: { id: userId, drawing_id: drawingId },
            headers: getApiHeaders(),
            withCredentials: true,
        });
    }, [userId, enabled, clearDebounce]);

    return {
        drawings,
        loading,
        error,
        createDrawing,
        updateDrawing,
        deleteDrawing,
    };
}
